import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Building2, Fish, MapPin, Orbit } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useGlobal } from '../providers/global-provider';
import type { Phase } from '@/app/utils/vision-biology';
import { t } from '@/app/utils/i18n';
import { useTranslation } from '@/app/hooks/useTranslation';

interface GlobalScopeBarProps {
  speciesOptions: string[];
  phaseOptions: Phase[];
  siteOptions: string[];
  tankOptions: string[];
  locked?: boolean;
}

const PHASE_KEYS: Record<Phase, string> = {
  larval: 'larval',
  juvenile: 'juvenile',
  'pre-grow': 'preGrow',
  adult: 'adult'
};

const triggerClass = [
  'h-10 min-w-0 rounded-xl border border-slate-200/85 bg-slate-50/90 px-3 text-sm font-semibold text-slate-900 shadow-sm',
  'hover:border-cyan-300 hover:bg-white hover:shadow-[0_8px_24px_-20px_rgba(14,165,233,0.85)]',
  'focus:ring-2 focus:ring-cyan-500/20 focus-visible:border-cyan-400',
  'dark:border-slate-700/70 dark:bg-slate-950/34 dark:text-slate-100 dark:hover:border-cyan-400/45 dark:hover:bg-slate-900/70',
  '[&_[data-slot=select-value]]:min-w-0 [&_svg]:self-center [&_svg:not([class*=text-])]:text-slate-500 dark:[&_svg:not([class*=text-])]:text-slate-400'
].join(' ');
const selectIconClass = 'mr-2 h-4 w-4 shrink-0 self-center text-muted-foreground';
const lockedScopeClass = [
  'flex h-10 min-w-0 items-center rounded-xl border border-slate-200/85 bg-slate-50/72 px-3 text-sm font-semibold text-slate-900 shadow-sm',
  'dark:border-slate-700/70 dark:bg-slate-950/24 dark:text-slate-100',
  '[&_svg:not([class*=text-])]:text-slate-500 dark:[&_svg:not([class*=text-])]:text-slate-400'
].join(' ');
const slugify = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');

export function GlobalScopeBar({ speciesOptions, phaseOptions, siteOptions, tankOptions, locked = false }: GlobalScopeBarProps) {
  const { lang } = useTranslation();
  const { clientSlug, projectSlug } = useParams<{ clientSlug?: string; projectSlug?: string }>();
  const {
    client,
    branding,
    availableClients,
    species,
    phase,
    site,
    tank,
    setClient,
    setSpecies,
    setPhase,
    setSite,
    setTank
  } = useGlobal();
  const routeClientSlug = clientSlug ? slugify(clientSlug) : null;
  const isRouteScoped = Boolean(routeClientSlug && projectSlug);
  const clientOptions = routeClientSlug
    ? availableClients.filter((item) => slugify(item.slug) === routeClientSlug || slugify(item.name) === routeClientSlug)
    : availableClients;
  const selectedClientId = clientOptions.some((item) => item.id === client?.id)
    ? client?.id
    : clientOptions[0]?.id;

  useEffect(() => {
    if (species && speciesOptions.length > 0 && !speciesOptions.includes(species)) {
      setSpecies(null);
    }
  }, [setSpecies, species, speciesOptions]);

  useEffect(() => {
    if (speciesOptions.length === 1 && species !== speciesOptions[0]) {
      setSpecies(speciesOptions[0]);
    }
  }, [setSpecies, species, speciesOptions]);

  useEffect(() => {
    if (phase && phaseOptions.length > 0 && !phaseOptions.includes(phase)) {
      setPhase(null);
    }
  }, [phase, phaseOptions, setPhase]);

  useEffect(() => {
    if (phaseOptions.length === 1 && phase !== phaseOptions[0]) {
      setPhase(phaseOptions[0]);
    }
  }, [phase, phaseOptions, setPhase]);

  useEffect(() => {
    if (site && siteOptions.length > 0 && !siteOptions.includes(site)) {
      setSite(null);
      setTank(null);
    }
  }, [setSite, setTank, site, siteOptions]);

  useEffect(() => {
    if (siteOptions.length === 1 && site !== siteOptions[0]) {
      setSite(siteOptions[0]);
      setTank(null);
    }
  }, [site, siteOptions, setSite, setTank]);

  useEffect(() => {
    if (tank && tankOptions.length > 0 && !tankOptions.includes(tank)) {
      setTank(null);
    }
  }, [setTank, tank, tankOptions]);

  useEffect(() => {
    if (tankOptions.length === 1 && tank !== tankOptions[0]) {
      setTank(tankOptions[0]);
    }
  }, [setTank, tank, tankOptions]);

  const hasMultipleSites = !locked && siteOptions.length > 1;
  const hasMultipleSpecies = !locked && speciesOptions.length > 1;
  const hasMultiplePhases = !locked && phaseOptions.length > 1;
  const hasMultipleTanks = !locked && tankOptions.length > 1;
  const selectedSite = site ?? (siteOptions.length === 1 ? siteOptions[0] : 'all');
  const selectedSpecies = species ?? (speciesOptions.length === 1 ? speciesOptions[0] : 'all');
  const selectedPhase = phase ?? (phaseOptions.length === 1 ? phaseOptions[0] : 'all');
  const selectedTank = tank ?? (tankOptions.length === 1 ? tankOptions[0] : 'all');
  const operationalAreaLabel = lang === 'en'
    ? 'Operational area'
    : lang === 'es'
      ? 'Zona operacional'
      : 'Zona operacional';
  const allOperationalAreasLabel = lang === 'en'
    ? 'All operational areas'
    : lang === 'es'
      ? 'Todas las zonas operacionales'
      : 'Todas as zonas operacionais';
  const renderLockedScope = (Icon: typeof MapPin, value: string) => (
    <div className="min-w-0">
      <div className={lockedScopeClass} aria-label={value} title={value}>
        <Icon className={selectIconClass} />
        <span className="min-w-0 truncate">{value}</span>
      </div>
    </div>
  );
  const locationLabel = selectedSite === 'all' ? t('vision', 'allFacilities', lang) : selectedSite;
  const speciesLabel = selectedSpecies === 'all' ? t('vision', 'allSpecies', lang) : selectedSpecies;
  const phaseLabel = selectedPhase === 'all' ? t('vision', 'allPhases', lang) : t('vision', PHASE_KEYS[selectedPhase as Phase], lang);
  const tankLabel = selectedTank === 'all' ? allOperationalAreasLabel : selectedTank;
  const locationSelect = (
    hasMultipleSites ? (
      <div className="min-w-0">
      <Select value={selectedSite} onValueChange={(value) => { setSite(value === 'all' ? null : value); setTank(null); }}>
        <SelectTrigger className={triggerClass}>
          <MapPin className={selectIconClass} />
          <SelectValue placeholder={t('vision', 'facility', lang)} />
        </SelectTrigger>
        <SelectContent>
          {hasMultipleSites ? <SelectItem value="all">{t('vision', 'allFacilities', lang)}</SelectItem> : null}
          {siteOptions.map((item) => (
            <SelectItem key={item} value={item}>{item}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      </div>
    ) : renderLockedScope(MapPin, locationLabel)
  );

  return (
    <section data-demo-tour="scope" className="aql-vision-scope-bar h-16 mb-0 bg-card transition-all duration-300">
      <div
        className="h-16 border-b bg-card px-4 transition-all duration-300 flex items-center"
        style={{
          borderColor: `${branding.primaryColor}20`
        }}
      >
        <div
          className={`grid w-full grid-flow-col gap-3 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
            isRouteScoped
              ? 'auto-cols-[minmax(11.5rem,1fr)] lg:grid-flow-row lg:grid-cols-4 lg:overflow-visible lg:pb-0'
              : 'auto-cols-[minmax(11.5rem,1fr)] lg:grid-flow-row lg:grid-cols-5 lg:overflow-visible lg:pb-0'
          }`}
        >
          {isRouteScoped ? locationSelect : (
            <div className="min-w-0">
              <Select
                value={selectedClientId ?? 'all'}
                onValueChange={(value) => {
                  const selectedClient = clientOptions.find((item) => item.id === value) ?? null;
                  setClient(selectedClient);
                  setSpecies(selectedClient?.species_focus ?? null);
                  setPhase((selectedClient?.default_phase as Phase | undefined) ?? null);
                  setSite(null);
                  setTank(null);
                }}
              >
                <SelectTrigger className={triggerClass}>
                  <Building2 className={selectIconClass} />
                  <SelectValue placeholder={t('vision', 'clientFacility', lang)} />
                </SelectTrigger>
                <SelectContent>
                  {clientOptions.map((item) => (
                    <SelectItem key={item.id} value={item.id}>{item.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {hasMultipleSpecies ? (
            <div className="min-w-0">
              <Select value={selectedSpecies} onValueChange={(value) => setSpecies(value === 'all' ? null : value)}>
                <SelectTrigger className={triggerClass}>
                  <Fish className={selectIconClass} />
                  <SelectValue placeholder={t('vision', 'species', lang)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('vision', 'allSpecies', lang)}</SelectItem>
                  {speciesOptions.map((item) => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : renderLockedScope(Fish, speciesLabel)}

          {hasMultiplePhases ? (
            <div className="min-w-0">
              <Select value={selectedPhase} onValueChange={(value) => setPhase(value === 'all' ? null : value as Phase)}>
                <SelectTrigger className={triggerClass}>
                  <Orbit className={selectIconClass} />
                  <SelectValue placeholder={t('vision', 'phase', lang)} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('vision', 'allPhases', lang)}</SelectItem>
                  {phaseOptions.map((item) => (
                    <SelectItem key={item} value={item}>{t('vision', PHASE_KEYS[item], lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : renderLockedScope(Orbit, phaseLabel)}

          {!isRouteScoped ? locationSelect : null}

          {hasMultipleTanks ? (
            <div className="min-w-0">
              <Select value={selectedTank} onValueChange={(value) => setTank(value === 'all' ? null : value)}>
                <SelectTrigger className={triggerClass}>
                  <MapPin className={selectIconClass} />
                  <SelectValue placeholder={operationalAreaLabel} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{allOperationalAreasLabel}</SelectItem>
                  {tankOptions.map((item) => (
                    <SelectItem key={item} value={item}>{item}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : renderLockedScope(MapPin, tankLabel)}
        </div>
      </div>
    </section>
  );
}
